import { Dictionary } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { Account } from 'api/account';
import { Role } from 'api/models';
import { RateCardVersion, RoleRate } from 'api/rate-cards';
import { useTranslation } from 'hooks/use-translation';
import { UpdateRateForCurrency } from '../../types';

interface Props {
  account: Account;
  roleLookup?: Dictionary<Role>;
  selectedRateCardVersion: RateCardVersion | undefined;
}

interface ReturnProps {
  roleRates: Dictionary<RoleRate>;
  resetRoleRate: () => void;
  resetToOriginalRoleRates: () => void;
  deleteRoleRate: (roleId: number) => void;
  updateRoleRate: (params: UpdateRateForCurrency) => void;
}

export const useRoleRates = ({
  account,
  roleLookup,
  selectedRateCardVersion,
}: Props): ReturnProps => {
  const defaultRate = useTranslation(
    'settings.rate_cards.drawer.table.row.default',
  );

  const defaultRoleRate: Dictionary<RoleRate> = useMemo(
    () => ({
      [-1]: {
        roleId: -1,
        roleName: defaultRate,
        multiCurrencyRates: {
          [account.currency.currencyCode]: {
            currency: account.currency,
            billRate: 0,
          },
        },
      },
    }),
    [account.currency, defaultRate],
  );

  const [roleRates, setRoleRates] =
    useState<Dictionary<RoleRate>>(defaultRoleRate);

  const resetRoleRate = useCallback(() => {
    setRoleRates(defaultRoleRate);
  }, [defaultRoleRate]);

  const resetToOriginalRoleRates = useCallback(() => {
    if (selectedRateCardVersion == null || roleLookup == null) {
      return;
    }

    // This check breaks right now when account currency does not match rate-card currency
    // const [defaultRateForDefaultCurrency] =
    //   selectedRateCardVersion.defaultRate.multiCurrencyRates.filter(
    //     ({ currency: { currencyCode } }) =>
    //       currencyCode === account.currency.currencyCode,
    //   );

    const [defaultRateForDefaultCurrency] =
      selectedRateCardVersion.defaultRate.multiCurrencyRates;

    setRoleRates({
      [-1]: {
        roleId: -1,
        roleName: defaultRate,
        multiCurrencyRates: {
          [defaultRateForDefaultCurrency.currency.currencyCode]:
            defaultRateForDefaultCurrency,
        },
      },
      ...selectedRateCardVersion.roleBillRateMapping.reduce(
        (result, rateMap) => {
          const [roleRatesForDefaultCurrency] =
            rateMap.multiCurrencyRates.filter(
              ({ currency: { currencyCode } }) =>
                currencyCode === account.currency.currencyCode,
            );

          return {
            ...result,
            ...(roleRatesForDefaultCurrency != null &&
            roleLookup[rateMap.roleId] != null
              ? {
                  [rateMap.roleId]: {
                    roleId: rateMap.roleId,
                    roleName: roleLookup[rateMap.roleId].roleName,
                    multiCurrencyRates: {
                      [roleRatesForDefaultCurrency.currency.currencyCode]:
                        roleRatesForDefaultCurrency,
                    },
                  },
                }
              : {}),
          };
        },
        {},
      ),
    });
  }, [
    account.currency.currencyCode,
    defaultRate,
    roleLookup,
    selectedRateCardVersion,
  ]);

  const deleteRoleRate = useCallback((roleId: number) => {
    setRoleRates(({ [roleId]: _, ...rest }) => rest);
  }, []);

  const updateRoleRate = useCallback(
    ({ roleId, roleName, currency, billRate }: UpdateRateForCurrency) => {
      setRoleRates((prev) => ({
        ...prev,
        [roleId]: {
          roleId,
          roleName,
          multiCurrencyRates: {
            ...(prev[roleId]?.multiCurrencyRates ?? {}),
            [currency.currencyCode]: {
              currency,
              billRate,
            },
          },
        },
      }));
    },
    [],
  );

  return {
    roleRates,
    resetRoleRate,
    resetToOriginalRoleRates,
    deleteRoleRate,
    updateRoleRate,
  };
};
